"use client";

import React, { useEffect, useId, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

type NotesCarouselImage = {
  src: string;
  alt: string;
  caption?: string;
};

type NotesImageCarouselProps = {
  images: NotesCarouselImage[];
  className?: string;
  autoPlayMs?: number;
  frameWidth?: number;
  frameHeight?: number;
};

export default function NotesImageCarousel({
  images,
  className,
  autoPlayMs,
  frameWidth,
  frameHeight,
}: NotesImageCarouselProps) {
  const safeImages = useMemo(() => images.filter(Boolean), [images]);
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselId = useId();

  const count = safeImages.length;
  const canNavigate = count > 1;

  useEffect(() => {
    if (!canNavigate) return;
    if (!autoPlayMs) return;

    const timer = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % count);
    }, autoPlayMs);

    return () => {
      window.clearInterval(timer);
    };
  }, [autoPlayMs, canNavigate, count]);

  if (activeIndex >= count && activeIndex !== 0) {
    setActiveIndex(0);
  }

  if (count === 0) return null;

  const activeImage = safeImages[activeIndex];

  const goPrev = () => {
    if (!canNavigate) return;
    setActiveIndex((prev) => (prev - 1 + count) % count);
  };

  const goNext = () => {
    if (!canNavigate) return;
    setActiveIndex((prev) => (prev + 1) % count);
  };

  return (
    <div
      className={className}
      role="region"
      aria-roledescription="carousel"
      aria-label="注意事項圖片輪播"
      style={
        frameWidth
          ? {
              maxWidth: `${frameWidth}px`,
              width: "100%",
            }
          : undefined
      }
    >
      <div
        className="relative overflow-hidden rounded-xl bg-white/80"
        aria-live={autoPlayMs ? "polite" : "off"}
      >
        {/* Image stage */}
        <div
          className={
            frameWidth && frameHeight
              ? "relative w-full"
              : "relative w-full h-64 sm:h-80"
          }
          style={
            frameWidth && frameHeight
              ? { aspectRatio: `${frameWidth} / ${frameHeight}` }
              : undefined
          }
        >
          {safeImages.map((img, index) => {
            const isActive = index === activeIndex;

            return (
              <img
                key={img.src}
                src={img.src}
                alt={img.alt}
                loading={isActive ? "eager" : "lazy"}
                decoding="async"
                className={
                  "absolute inset-0 w-full h-full object-contain transition-opacity duration-300 " +
                  (isActive ? "opacity-100" : "opacity-0")
                }
                aria-hidden={!isActive}
              />
            );
          })}
        </div>

        {/* Caption */}
        {activeImage?.caption ? (
          <div className="text-center px-4 py-3 text-sm text-gray-700">
            {activeImage.caption}
          </div>
        ) : null}

        {/* Controls */}
        {canNavigate ? (
          <>
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 backdrop-blur px-3 py-2 shadow hover:bg-white transition-colors"
              aria-label="上一張"
              aria-controls={carouselId}
            >
              <ChevronLeft className="w-5 h-5 text-[var(--blue-900)]" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/90 backdrop-blur px-3 py-2 shadow hover:bg-white transition-colors"
              aria-label="下一張"
              aria-controls={carouselId}
            >
              <ChevronRight className="w-5 h-5 text-[var(--blue-900)]" aria-hidden="true" />
            </button>
          </>
        ) : null}
      </div>

      {/* Dots */}
      {canNavigate ? (
        <div
          id={carouselId}
          className="mt-3 flex items-center justify-center gap-2"
        >
          {safeImages.map((img, index) => {
            const isActive = index === activeIndex;
            return (
              <button
                key={img.src + "-dot"}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={
                  "h-2.5 rounded-full transition-all " +
                  (isActive
                    ? "w-7 bg-[var(--blue-700)]"
                    : "w-2.5 bg-gray-300 hover:bg-gray-400")
                }
                aria-label={`切換到第 ${index + 1} 張`}
                aria-current={isActive}
              />
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
